import clsx from 'clsx';
import React from 'react';
import { InView } from 'react-intersection-observer';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import { SecondaryButton } from '@components/Button/variants';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { ClickTarget, ContentType, ImpressionElement, click, impression } from '@tracking';
import { hasUserOptedInToTrackingConsent } from '@utils/consent';

import { Advertisement } from '../../interfaces/homepage';
import styles from './styles.module.scss';

interface VerticalAdvertisementProps {
  advertisement: Advertisement;
  className?: string;
  trackingLocation: string;
}

const VerticalAdvertisement = ({
  advertisement,
  className,
  trackingLocation,
}: VerticalAdvertisementProps) => {
  const hydrated = useServerSideRendering();
  const { Track, trackEvent } = useEventTracking({
    contentType: ContentType.Advertisement,
    contentId: advertisement.id,
    linkedAdScheduleId: advertisement.adScheduleId,
    location: trackingLocation,
  });

  return (
    <Track>
      <InView
        triggerOnce={true}
        onChange={(inView) => {
          inView &&
            trackEvent(impression({ impressionElement: ImpressionElement.FeaturedPartnerAd }));
        }}
      >
        <aside
          className={clsx(className, 'relative mb-64 w-full bg-grayscale-beige pt-48')}
          data-testid="featured-partner-vertical"
        >
          <div className="flex flex-col items-start justify-between gap-16 px-20 pb-20 pt-16">
            <div
              className={clsx(
                styles.featuredPartnerContainer,
                'absolute right-0 top-0 w-[150px] bg-primary-magenta py-8 pr-16 text-right font-suisse-intl-mono text-[9px] text-white'
              )}
            >
              Featured Partner
            </div>
            <div className="flex w-full justify-start">
              <LazyLoadImage
                src={advertisement.sponsorLogo}
                alt={`${advertisement.sponsorName} logo`}
                height={40}
                className="h-40"
              />
            </div>
            <p className="!my-0 font-suisse-neue text-body-sm text-grayscale-charcoal">
              {advertisement.header}
            </p>
            <p className="!my-0">
              <SecondaryButton
                as="externalLink"
                href={advertisement.clickthroughUrl}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.FeaturedPartnerAd }))
                }
                rel="noreferrer sponsored"
                size="small"
              >
                Learn more
              </SecondaryButton>
            </p>
          </div>
          {hydrated && advertisement.trackingPixelUrl && hasUserOptedInToTrackingConsent() && (
            <img src={advertisement.trackingPixelUrl} className="hidden" />
          )}
        </aside>
      </InView>
    </Track>
  );
};

export default VerticalAdvertisement;
